/**
 * Router (RFC §6, NORMATIVE) — URL ↔ URN routes, outlets, guards.
 * Every URN-addressed surface is deep-linkable; route state is a signal.
 *
 * ── Matching (PINNED) ──
 * Patterns are path-to-pattern strings (`/suche/stelle/:id`) — NO regex in app
 * code. Top-level patterns are absolute; `children` patterns are RELATIVE to
 * their parent. Precedence: depth-first, declaration order, FIRST COMPLETE
 * match wins; a parent that consumes the full path is itself a leaf match
 * (its outlet stays empty) and is preferred over its children.
 * `*` as the FINAL pattern segment is a catch-all: it consumes the (possibly
 * empty) remainder without capturing, and the record's children are ignored —
 * declare it LAST. The canonical use is an in-shell fallback child
 * (`{pattern: "*", surface: "app-not-found"}`), so unknown urls render INSIDE
 * the layout (shell VM + DOM survive) instead of replacing it; the
 * options-level `notFound` remains the root fallback for guard notFound and
 * trees without a catch-all. Static pattern
 * segments compare against the RAW (still-encoded) path segment; `:param`
 * captures are decoded via the codec's `decodeUrnId` (entity ids round-trip;
 * `~` in a param segment always means the in-segment separator). A segment
 * with malformed percent-encoding fails that candidate match. Matching uses
 * the pathname only; `search`/`hash` ride along on the match object.
 *
 * ── Guards ──
 * Run root→leaf, awaited in order, BEFORE anything mounts (and before the
 * history commit). `pending` is true while a chain runs. `redirect` honors the
 * RFC's replace-not-push rule in commit terms: the ATTEMPTED url NEVER becomes
 * a history entry — the redirect target inherits the original navigation's
 * history op (a push stays ONE push, so Back returns to where the user came
 * from, never to the guarded url; a popstate-triggered redirect REPLACES the
 * entry to repair it). Capped at 10 consecutive redirects (then not-found +
 * console.error — no loops). `notFound` (and any unmatched URL) mounts the
 * app-registered fallback surface — no silent empty shells.
 *
 * ── Mounting / outlets ──
 * The matched chain mounts root→leaf: depth 0 into `options.root` (its
 * children are ROUTER-OWNED and replaced), depth n into the `<kit-outlet>`
 * rendered by the depth n−1 surface. Between depths the router awaits the
 * render scheduler (`flushed()`), so a KitElement parent has rendered its
 * outlet before the child mounts. Navigating between siblings swaps ONLY the
 * child — the parent element instance (VM + DOM) survives.
 *
 * Element reuse (PINNED): same record + same params → element kept untouched.
 * Same record + changed params → when the record declares `urn:`, the element
 * is KEPT and its `urn` attribute updated (KitElement's §4 rebind takes over);
 * WITHOUT `urn:` the element is REPLACED (params are its input; there is no
 * rebind seam). Different record → replaced, along with everything deeper.
 *
 * ── Scroll restoration (PINNED) ──
 * `history.scrollRestoration = "manual"` (feature-detected). Every history
 * entry the router creates carries `{__kitRoute: <id>}` in `history.state`;
 * scroll positions live in an in-memory map keyed by that id (lost on full
 * reload — standard SPA behavior). On push/replace the LEAVING entry's scroll
 * is recorded first; after mount, a push scrolls to top — or to `#fragment`
 * via `getElementById().scrollIntoView()` when present. On popstate the
 * leaving entry's scroll is recorded (with manual restoration the browser has
 * not touched it yet), then the target entry's recorded position is restored
 * (top when unknown).
 *
 * ── Concurrency ──
 * Navigations are sequenced: a navigate() during a pending guard chain wins —
 * the older resolution abandons before mounting.
 *
 * Link interception is NOT router-core: apps use plain `<a>` + a delegated
 * click handler of their own (or data-act verbs) calling `navigate()`.
 * `back()` is a history passthrough.
 *
 * DOM-free import guarantee: importing this module needs no DOM globals;
 * `createRouter` construction requires a real `root` Element (its
 * `ownerDocument` supplies createElement/getElementById — no document seam
 * needed). history/location/window are injectable seams for tests.
 */

import { signal, type ReadonlySignal } from "../core/signal";
import { flushed } from "../core/scheduler";
import type { Urn } from "../core/element";
import type { TagName } from "../core/define";
import { decodeUrnId } from "./codec";

export type Params = Readonly<Record<string, string>>;

export type GuardResult =
  | { kind: "allow" }
  | { kind: "redirect"; url: string }
  | { kind: "notFound" };

/** What a guard sees (PINNED). `params` are cumulative root→its own depth. */
export interface GuardCtx {
  params: Params;
  /** The full target url (pathname + search + hash). */
  url: string;
  /** Present when the guarded record declares `urn:`. */
  urn?: Urn;
}

export interface RouteRecord {
  /** Absolute at the top level (`/suche/stelle/:id`); RELATIVE in `children`. */
  pattern: string;
  /** Element mounted for this record. */
  surface: TagName;
  /** URL → URN binding for entity routes. */
  urn?: (params: Params) => Urn;
  /** Async gate, runs before the subtree mounts. */
  guard?: (ctx: GuardCtx) => Promise<GuardResult>;
  children?: RouteRecord[];
}

export interface MatchedRecord {
  record: RouteRecord;
  /** Cumulative params root→this depth. */
  params: Params;
  urn?: Urn;
}

export interface RouteMatch {
  /** pathname + search + hash. */
  url: string;
  /** pathname only. */
  path: string;
  /** Merged params of the full chain. */
  params: Params;
  /** Root→leaf. Empty when status is "notFound". */
  chain: ReadonlyArray<MatchedRecord>;
  status: "ok" | "notFound";
}

// ── seams ────────────────────────────────────────────────────────────────────

export interface HistoryLike {
  state: unknown;
  pushState(state: unknown, unused: string, url: string): void;
  replaceState(state: unknown, unused: string, url: string): void;
  scrollRestoration?: "auto" | "manual";
  back?(): void;
}

export interface LocationLike {
  pathname: string;
  search: string;
  hash: string;
}

export interface WindowLike {
  addEventListener(type: string, fn: (ev: { state?: unknown }) => void): void;
  removeEventListener(type: string, fn: (ev: { state?: unknown }) => void): void;
  scrollTo(x: number, y: number): void;
  scrollX: number;
  scrollY: number;
}

export interface RouterOptions {
  routes: RouteRecord[];
  /** Router-owned mount container; its children are replaced by matched surfaces. */
  root: Element;
  /** The app-registered not-found fallback surface. */
  notFound: TagName;
  history?: HistoryLike;
  location?: LocationLike;
  window?: WindowLike;
}

export interface Router {
  /** Route state (null before start()). Layouts derive aria-current from it. */
  readonly current: ReadonlySignal<RouteMatch | null>;
  /** True while a guard chain / mount is in flight. */
  readonly pending: ReadonlySignal<boolean>;
  /** Navigate to a same-origin url ("/path?x=1#frag"). Resolves after mount. */
  navigate(url: string, opts?: { replace?: boolean }): Promise<void>;
  back(): void;
  /** Install popstate + scrollRestoration and resolve the current location. */
  start(): Promise<void>;
  /** Remove listeners. Mounted surfaces stay (app teardown owns the DOM). */
  dispose(): void;
}

// ── pure matching (exported for tests + app tooling) ─────────────────────────

const toSegments = (pattern: string): string[] =>
  pattern.split("/").filter((s) => s !== "");

/** Match one pattern against path segments at `offset`. Returns captures or null. */
const matchAt = (
  patternSegs: readonly string[],
  segments: readonly string[],
  offset: number
): Record<string, string> | null => {
  if (offset + patternSegs.length > segments.length) return null;
  const captured: Record<string, string> = {};
  for (let i = 0; i < patternSegs.length; i++) {
    const pat = patternSegs[i]!;
    const seg = segments[offset + i]!;
    if (pat.startsWith(":")) {
      try {
        captured[pat.slice(1)] = decodeUrnId(seg);
      } catch {
        return null; // malformed/empty segment fails this candidate
      }
    } else if (pat !== seg) {
      return null;
    }
  }
  return captured;
};

/**
 * Pure route matching: depth-first, declaration order, first complete match.
 * Returns the root→leaf chain or null.
 */
export function matchRoutes(
  routes: readonly RouteRecord[],
  path: string
): MatchedRecord[] | null {
  const segments = path.split("/").filter((s) => s !== "");

  const walk = (
    records: readonly RouteRecord[],
    offset: number,
    inherited: Params
  ): MatchedRecord[] | null => {
    for (const record of records) {
      const patternSegs = toSegments(record.pattern);

      // `*` catch-all (final segment only): consumes the whole remainder.
      if (patternSegs.at(-1) === "*") {
        const staticSegs = patternSegs.slice(0, -1);
        const captured = matchAt(staticSegs, segments, offset);
        if (!captured) continue;
        const params: Params = { ...inherited, ...captured };
        return [
          {
            record,
            params,
            ...(record.urn ? { urn: record.urn(params) } : {}),
          },
        ];
      }

      const captured = matchAt(patternSegs, segments, offset);
      if (!captured) continue;
      const params: Params = { ...inherited, ...captured };
      const consumed = offset + patternSegs.length;
      const entry: MatchedRecord = {
        record,
        params,
        ...(record.urn ? { urn: record.urn(params) } : {}),
      };
      if (consumed === segments.length) return [entry]; // full consume → leaf
      if (record.children) {
        const rest = walk(record.children, consumed, params);
        if (rest) return [entry, ...rest];
      }
    }
    return null;
  };

  return walk(routes, 0, {});
}

// ── <kit-outlet> ─────────────────────────────────────────────────────────────

/** Lazily-resolved base so importing this module never requires DOM globals. */
const HTMLElementBase: typeof HTMLElement =
  (globalThis as { HTMLElement?: typeof HTMLElement }).HTMLElement ??
  (class {
    constructor() {
      throw new Error(
        "[web-kit] <kit-outlet> requires a DOM environment (HTMLElement is not defined)."
      );
    }
  } as unknown as typeof HTMLElement);

/** The nesting seam: a matched parent renders it; the router mounts the child inside. */
export class KitOutlet extends HTMLElementBase {}

/** Register `<kit-outlet>` (idempotent, explicit — no import side effects). */
export function defineOutlet(): void {
  if (!customElements.get("kit-outlet")) {
    customElements.define("kit-outlet", KitOutlet);
  }
}

// ── router ───────────────────────────────────────────────────────────────────

const MAX_REDIRECTS = 10;

interface MountedLevel {
  record: RouteRecord;
  params: Params;
  el: Element;
}

const paramsEqual = (a: Params, b: Params): boolean => {
  const ak = Object.keys(a);
  const bk = Object.keys(b);
  return ak.length === bk.length && ak.every((k) => a[k] === b[k]);
};

const splitUrl = (url: string): { path: string; hash: string } => {
  const hashIdx = url.indexOf("#");
  const hash = hashIdx >= 0 ? url.slice(hashIdx + 1) : "";
  const beforeHash = hashIdx >= 0 ? url.slice(0, hashIdx) : url;
  const queryIdx = beforeHash.indexOf("?");
  const path = queryIdx >= 0 ? beforeHash.slice(0, queryIdx) : beforeHash;
  return { path, hash };
};

export function createRouter(options: RouterOptions): Router {
  const { routes, root, notFound } = options;
  const doc = root.ownerDocument;
  const g = globalThis as {
    history?: HistoryLike;
    location?: LocationLike;
    window?: WindowLike;
  };
  const history = options.history ?? g.history;
  const location = options.location ?? g.location;
  const win = options.window ?? g.window;
  if (!history || !location || !win) {
    throw new Error(
      "[web-kit] createRouter: no history/location/window available — " +
        "inject the seams or run in a DOM environment."
    );
  }

  const current = signal<RouteMatch | null>(null);
  const pending = signal(false);

  let seq = 0; // navigation sequence; stale resolutions abandon
  let entrySeq = 0;
  let currentEntryId: string | null = null;
  const scrollPositions = new Map<string, { x: number; y: number }>();
  const mounted: MountedLevel[] = [];
  let onPopstate: ((ev: { state?: unknown }) => void) | null = null;

  const newEntryId = (): string => `kit:${++entrySeq}`;

  const entryIdOf = (state: unknown): string | null =>
    state && typeof state === "object" && "__kitRoute" in state
      ? String((state as { __kitRoute: unknown }).__kitRoute)
      : null;

  const recordScroll = (): void => {
    if (currentEntryId !== null) {
      scrollPositions.set(currentEntryId, { x: win.scrollX, y: win.scrollY });
    }
  };

  const teardownFrom = (depth: number): void => {
    while (mounted.length > depth) {
      mounted.pop()!.el.remove();
    }
  };

  const mountNotFound = (): void => {
    teardownFrom(0);
    root.replaceChildren(doc.createElement(notFound));
  };

  const mountChain = async (chain: readonly MatchedRecord[]): Promise<void> => {
    let container: Element = root;
    for (let depth = 0; depth < chain.length; depth++) {
      const entry = chain[depth]!;
      const existing = mounted[depth];
      const sameRecord = existing?.record === entry.record;
      const keep =
        sameRecord &&
        (paramsEqual(existing!.params, entry.params) ||
          entry.record.urn !== undefined);

      let el: Element;
      if (keep) {
        el = existing!.el;
        existing!.params = entry.params;
        if (entry.urn && el.getAttribute("urn") !== entry.urn) {
          el.setAttribute("urn", entry.urn); // KitElement rebind (§4)
        }
      } else {
        teardownFrom(depth);
        el = doc.createElement(entry.record.surface);
        if (entry.urn) el.setAttribute("urn", entry.urn);
        container.replaceChildren(el);
        mounted.push({ record: entry.record, params: entry.params, el });
      }

      if (depth < chain.length - 1) {
        await flushed(); // let the parent render its outlet
        const outlet = el.querySelector("kit-outlet");
        if (!outlet) {
          console.error(
            `[web-kit] router: <${entry.record.surface}> matched with a child ` +
              `route but rendered no <kit-outlet>; the child cannot mount.`
          );
          teardownFrom(depth + 1);
          return;
        }
        container = outlet;
      }
    }
    teardownFrom(chain.length);
  };

  const applyScroll = (mode: "top" | "restore", hash: string): void => {
    if (hash) {
      doc.getElementById(hash)?.scrollIntoView();
      return;
    }
    if (mode === "restore" && currentEntryId !== null) {
      const pos = scrollPositions.get(currentEntryId);
      win.scrollTo(pos?.x ?? 0, pos?.y ?? 0);
      return;
    }
    win.scrollTo(0, 0);
  };

  /** Resolve url → guards → history op → mount → scroll. The one pipeline. */
  const resolve = async (
    url: string,
    historyOp: "push" | "replace" | "none",
    scrollMode: "top" | "restore",
    redirectDepth: number
  ): Promise<void> => {
    const mySeq = ++seq;
    pending.set(true);
    try {
      const { path, hash } = splitUrl(url);
      const chain = matchRoutes(routes, path);
      let status: RouteMatch["status"] = chain ? "ok" : "notFound";

      if (chain) {
        for (const { record, params, urn } of chain) {
          if (!record.guard) continue;
          const result = await record.guard({
            params,
            url,
            ...(urn ? { urn } : {}),
          });
          if (seq !== mySeq) return; // a newer navigation won
          if (result.kind === "redirect") {
            if (redirectDepth >= MAX_REDIRECTS) {
              console.error(
                `[web-kit] router: redirect limit (${MAX_REDIRECTS}) reached at "${url}" — mounting not-found.`
              );
              status = "notFound";
              break;
            }
            await resolve(
              result.url,
              historyOp === "none" ? "replace" : historyOp,
              "top",
              redirectDepth + 1
            );
            return;
          }
          if (result.kind === "notFound") {
            status = "notFound";
            break;
          }
        }
      }
      if (seq !== mySeq) return;

      // History op + scroll bookkeeping (record the LEAVING entry first).
      if (historyOp !== "none") {
        recordScroll();
        const id = newEntryId();
        const state = { __kitRoute: id };
        if (historyOp === "push") history.pushState(state, "", url);
        else history.replaceState(state, "", url);
        currentEntryId = id;
      }

      if (status === "ok" && chain) {
        await mountChain(chain);
        if (seq !== mySeq) return;
        current.set({
          url,
          path,
          params: chain.at(-1)?.params ?? {},
          chain,
          status,
        });
      } else {
        mountNotFound();
        current.set({ url, path, params: {}, chain: [], status: "notFound" });
      }

      applyScroll(scrollMode, hash);
    } finally {
      if (seq === mySeq) pending.set(false);
    }
  };

  const currentUrl = (): string =>
    `${location.pathname}${location.search}${location.hash}`;

  return {
    current,
    pending,

    navigate: (url, opts) =>
      resolve(url, opts?.replace ? "replace" : "push", "top", 0),

    back(): void {
      history.back?.();
    },

    async start(): Promise<void> {
      if ("scrollRestoration" in history && history.scrollRestoration) {
        history.scrollRestoration = "manual";
      }
      onPopstate = (ev) => {
        recordScroll(); // manual restoration: the browser hasn't moved us yet
        currentEntryId = entryIdOf(ev.state ?? history.state);
        void resolve(currentUrl(), "none", "restore", 0);
      };
      win.addEventListener("popstate", onPopstate);
      // Adopt the current entry (replace so it carries a router entry id).
      await resolve(currentUrl(), "replace", "top", 0);
    },

    dispose(): void {
      if (onPopstate) {
        win.removeEventListener("popstate", onPopstate);
        onPopstate = null;
      }
    },
  };
}
